import React, { useContext, createContext, useState } from "react"
import { ClipboardDocumentIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline'

/**
 * @fileoverview Composant EmailLink pour afficher les adresses email avec un lien mailto
 *
 * Ce composant permet d'afficher une adresse email sous forme de lien mailto cliquable,
 * avec une icône optionnelle et des styles personnalisables via DaisyUI et TailwindCSS.
 * Il utilise Context API pour permettre une personnalisation globale.
 *
 * @example Utilisation basique
 * ```jsx
 * import EmailLink from '../../components/EmailLink'
 *
 * // Dans un composant
 * <EmailLink email="contact@example.com" />
 * ```
 *
 * @example Avec sujet et corps pré-remplis
 * ```jsx
 * <EmailLink
 *   email="contact@example.com"
 *   showSubject={true}
 *   subject="Demande d'information"
 *   body="Bonjour,\n\nJe souhaiterais obtenir plus d'informations sur..."
 * />
 * ```
 *
 * @example Personnalisation via contexte
 * ```jsx
 * import { EmailProvider } from '../../components/EmailLink'
 *
 * // Dans un composant parent
 * <EmailProvider options={{ showIcon: false, linkClassName: "link link-secondary" }}>
 *   {// Tous les EmailLink à l'intérieur auront ces options }
 *   <SomeComponent />
 * </EmailProvider>
 * ```
 *
 * @example Personnalisation via classes CSS
 * ```jsx
 * <EmailLink
 *   email="contact@example.com"
 *   className="pl-2 text-lg font-bold"
 * />
 * ```
 */

// Création d'un contexte pour les options d'email
const EmailContext = createContext({
  showIcon: true,
  tooltipText: "Envoyer un email",
  linkClassName: "link no-underline hover:underline hover:link-primary transition-all"
})

// Hook personnalisé pour utiliser le contexte d'email
export const useEmailContext = () => useContext(EmailContext)

/**
 * Provider pour personnaliser globalement les options des liens email
 *
 * @param {Object} props - Propriétés du composant
 * @param {React.ReactNode} props.children - Composants enfants
 * @param {Object} props.options - Options de personnalisation
 * @param {boolean} [props.options.showIcon] - Afficher ou non l'icône d'email
 * @param {string} [props.options.tooltipText] - Texte du tooltip
 * @param {string} [props.options.linkClassName] - Classes CSS pour le lien
 *
 * @returns {JSX.Element} Provider pour le contexte d'email
 */
export const EmailProvider = ({ children, options }) => {
  return (
    <EmailContext.Provider value={{ ...useEmailContext(), ...options }}>
      {children}
    </EmailContext.Provider>
  )
}

/**
 * Composant pour afficher une adresse email sous forme de lien mailto
 *
 * @param {Object} props - Propriétés du composant
 * @param {string} props.email - Adresse email à afficher
 * @param {string} [props.className=""] - Classes CSS supplémentaires pour le conteneur
 * @param {boolean} [props.showSubject=false] - Inclure le sujet dans le lien mailto
 * @param {string} [props.subject=""] - Sujet pré-rempli pour le lien mailto
 * @param {string} [props.body=""] - Corps du message pré-rempli pour le lien mailto
 *
 * @returns {JSX.Element|null} Lien mailto ou null si l'email est invalide
 */
const EmailLink = ({ email, className = "", showSubject = false, subject = "", body = "" }) => {
   const { showIcon, tooltipText, linkClassName } = useEmailContext()
   const [copied, setCopied] = useState(false)
  
  if (!email) return null
  
  // Validation simple de l'email
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  if (!isValidEmail) return <span className="text-error">{email}</span>
  
  // Construction du lien mailto avec paramètres optionnels
  let mailtoLink = `mailto:${email}`
  const params = []
  
  if (showSubject && subject) {
    params.push(`subject=${encodeURIComponent(subject)}`)
  }
  
  if (body) {
    params.push(`body=${encodeURIComponent(body)}`)
  }
  
   if (params.length > 0) {
     mailtoLink += `?${params.join('&')}`
   }

   const handleCopy = async (e) => {
     e.preventDefault()
     e.stopPropagation()
     try {
       await navigator.clipboard.writeText(email)
       setCopied(true)
       setTimeout(() => setCopied(false), 1500)
     } catch {
       setCopied(false)
     }
   }

   return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      {showIcon && (
        <div className="tooltip tooltip-primary" data-tip={tooltipText}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
      )}
      <a 
        href={mailtoLink} 
        className={linkClassName}
        onClick={(e) => e.stopPropagation()} // Empêche la propagation du clic pour éviter les conflits avec les rows cliquables
        target="_blank"
        rel="noopener noreferrer"
      >
        {email}
      </a>
      <button
        type="button"
        onClick={handleCopy}
        className="btn btn-xs btn-outline btn-square p-0.5"
        title={copied ? "Copié" : "Copier l'adresse email"}
        aria-label={copied ? "Copié" : "Copier l'adresse email"}
      >
        {copied ? <ClipboardDocumentCheckIcon className="h-3.5 w-3.5" /> : <ClipboardDocumentIcon className="h-3.5 w-3.5" />}
      </button>

    </div>
  )
}

export default EmailLink